import { getConnectorClient } from 'wagmi/actions'
import type { Connector } from 'wagmi'
import {
  createWalletClient,
  custom,
  walletActions,
  type Address,
  type Chain,
  type EIP1193Provider,
  type WalletClient,
} from 'viem'

import { asChainId } from '../../sdk/chainId'
import { tMsg } from '@/features/locale/translate'
import { infiniteCreativeChain, wagmiConfig } from '../../wagmi/config'

function extendWalletClient(client: WalletClient): WalletClient {
  return client.extend(walletActions) as WalletClient
}

/** Cadena viem usada en acciones de firma de la app. */
export function getAppSigningChain(chainId: number): Chain {
  if (infiniteCreativeChain.id !== chainId) return infiniteCreativeChain
  return infiniteCreativeChain
}

async function getConnectorProvider(connector: Connector, chainId: number): Promise<EIP1193Provider> {
  try {
    return (await connector.getProvider({ chainId })) as EIP1193Provider
  } catch {
    /* MetaMask móvil: getProvider({ chainId }) puede fallar en redes custom */
  }

  return (await connector.getProvider()) as EIP1193Provider
}

async function createWalletClientFromProvider(
  connector: Connector,
  chainId: number,
  account?: Address,
): Promise<WalletClient | undefined> {
  try {
    const [provider, accounts] = await Promise.all([
      getConnectorProvider(connector, chainId),
      connector.getAccounts(),
    ])
    const signingAccount = account ?? accounts[0]
    if (!signingAccount) return undefined

    return createWalletClient({
      account: signingAccount,
      chain: getAppSigningChain(chainId),
      transport: custom(provider),
    })
  } catch {
    return undefined
  }
}

/** Resuelve el cliente de firma del conector activo con cadena explícita (MetaMask móvil). */
export async function resolveWalletClient(
  connector: Connector | undefined,
  cachedClient: WalletClient | undefined,
  chainId: number,
  account?: Address,
): Promise<WalletClient | undefined> {
  if (!connector) return undefined

  try {
    const client = await getConnectorClient(wagmiConfig, {
      connector,
      chainId: asChainId(chainId),
      account,
      assertChainId: false,
    })
    return extendWalletClient(client as unknown as WalletClient)
  } catch {
    /* fallback below */
  }

  const fromProvider = await createWalletClientFromProvider(connector, chainId, account)
  if (fromProvider) return fromProvider

  if (cachedClient) return cachedClient

  return undefined
}

export function describeWalletSigningError(input: {
  account?: string | null
  connector?: Connector
  chainId?: number
  publicClient?: unknown
  trade?: unknown
}): string {
  const { wallet } = tMsg()
  if (!input.account) return wallet.error.connectToContinue
  if (!input.trade) return wallet.error.noValidOperation
  if (!input.chainId || !input.publicClient) {
    return wallet.error.networkUnavailable
  }
  if (!input.connector) {
    return wallet.error.connectorNotFound
  }
  return wallet.error.signingPrepareFailed
}
